// Parse an intuitively typed parking duration (Czech or English) into minutes.
//
// Accepts things people actually type:
//   "2h" "2 h" "2 hod" "2 hodiny" "2 hours" "1.5h" "1,5 h" "2h30" "2:30"
//   "90" "90 min" "90 minut" "půl hodiny" "hodina"
//   "do 18" "do 18:00" "until 18:30"      -> until that clock time (next occurrence)
//   "přes noc" "overnight" "do rána"      -> until 08:00 (next occurrence)
//   "celý den" "all day"                  -> 24 h
//   bare number: <= 12 reads as hours, otherwise as minutes ("2" = 2 h, "90" = 90 min)
//
// Returns { ok, minutes, kind: 'for'|'until', target?: 'HH:MM' }.

import { pragueParts } from './time.js'

const MAX_MINUTES = 72 * 60

/** Minutes from `nowMin` until the next occurrence of `targetMin` (minutes-of-day). */
function untilNext(nowMin, targetMin) {
  let d = targetMin - nowMin
  if (d <= 0) d += 1440
  return d
}

function hhmm(min) {
  const m = ((min % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/**
 * @param {string} input
 * @param {Date} [refDate] "now" for relative forms like "do 18:00"
 */
export function parseDuration(input, refDate = new Date()) {
  const fail = { ok: false, minutes: 0, kind: 'for' }
  if (typeof input !== 'string') return fail
  const s = input
    .toLowerCase()
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .trim()
  if (!s) return fail

  const nowMin = pragueParts(refDate).minutesOfDay
  const clamp = (m) => Math.max(5, Math.min(MAX_MINUTES, Math.round(m)))

  // --- "until <clock>" forms -------------------------------------------
  const until = s.match(/^(?:do|until|til|till)\s*(\d{1,2})(?:[:.](\d{2}))?\s*(?:h|hod(?:in)?)?$/)
  if (until) {
    const h = Number(until[1])
    const m = Number(until[2] || 0)
    if (h > 24 || m > 59) return fail
    const target = (h % 24) * 60 + m
    return { ok: true, minutes: clamp(untilNext(nowMin, target)), kind: 'until', target: hhmm(target) }
  }

  // overnight / until morning -> next 08:00
  if (/^(p[řr]es noc|overnight|do r[áa]na|till morning|until morning)$/.test(s)) {
    return { ok: true, minutes: clamp(untilNext(nowMin, 8 * 60)), kind: 'until', target: '08:00' }
  }

  // whole day
  if (/^(cel[ýy] den|all day|whole day|24 ?h|cel[ýy] ?den)$/.test(s)) {
    return { ok: true, minutes: 24 * 60, kind: 'for' }
  }

  // half an hour / an hour
  if (/^(p[ůu]l hodiny|p[ůu]lhodina|half an hour|half hour)$/.test(s)) {
    return { ok: true, minutes: 30, kind: 'for' }
  }
  if (/^(hodina|hodinu|jedna hodina|an hour|one hour|hour)$/.test(s)) {
    return { ok: true, minutes: 60, kind: 'for' }
  }

  // --- "2h30" / "2:30" (hours + minutes) --------------------------------
  const hm = s.match(/^(\d{1,2})\s*(?:h|hod(?:in[ayu]?)?|:)\s*(\d{1,2})\s*(?:m|min(?:ut[ay]?)?)?$/)
  if (hm) {
    const mins = Number(hm[1]) * 60 + Number(hm[2])
    return { ok: true, minutes: clamp(mins), kind: 'for' }
  }

  // --- "<n> hours" ------------------------------------------------------
  const hOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:h|hr?s?|hod(?:in[ayu]?)?|hours?)$/)
  if (hOnly) return { ok: true, minutes: clamp(Number(hOnly[1]) * 60), kind: 'for' }

  // --- "<n> minutes" ----------------------------------------------------
  const mOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:m|mins?|minut[ay]?|minutes?)$/)
  if (mOnly) return { ok: true, minutes: clamp(Number(mOnly[1])), kind: 'for' }

  // --- bare number: <=12 -> hours, else minutes -------------------------
  const bare = s.match(/^(\d+(?:\.\d+)?)$/)
  if (bare) {
    const n = Number(bare[1])
    if (n <= 0) return fail
    return { ok: true, minutes: clamp(n <= 12 ? n * 60 : n), kind: 'for' }
  }

  return fail
}
